import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Dialog } from "primereact/dialog";
import { Divider } from "primereact/divider";
import { Dropdown } from "primereact/dropdown";
import { FloatLabel } from "primereact/floatlabel";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import ConfirmationWrapper from "../common/ConfirmationWrapper";
import Loading from "../common/Loading";
import NoContent from "../common/NoContent";
import PageTitle from "../common/PageTitle";
import ProgressiveControl from "../common/ProgressiveControl";
import TabHeader from "../common/TabHeader";
import { useAppContext } from "../../providers/ProviderAppContainer";
import { TEXT_NORMAL, TEXT_SMALL } from "../../style";
import FieldNote from "./common/FieldNote";
import WebsiteMediaInput from "./WebsiteMediaInput";

const FILE_KINDS = [
    { label: "PDF", value: "PDF" },
    { label: "Word", value: "Word" },
    { label: "PowerPoint", value: "PowerPoint" },
    { label: "Excel", value: "Excel" },
    { label: "Image", value: "Image" },
    { label: "ZIP", value: "ZIP" },
    { label: "Other", value: "File" },
];

function folderName(value) {
    try {
        return decodeURIComponent(value || "General");
    } catch {
        return value || "General";
    }
}

export default function WebsiteBrochureFolder() {
    const { courseKey } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const { requestAPI, showToast } = useAppContext();
    const course = folderName(courseKey);
    const [loading, setLoading] = useState();
    const [error, setError] = useState();
    const [rows, setRows] = useState();
    const [folder, setFolder] = useState({
        name: course,
        description: location.state?.course_description || "",
        image: location.state?.course_image || "",
    });
    const [dialog, setDialog] = useState({ visible: false });
    const [folderDialog, setFolderDialog] = useState(false);

    const loadRows = useCallback(() => {
        requestAPI({
            requestPath: "website/brochures",
            requestMethod: "GET",
            setLoading,
            onRequestFailure: setError,
            onResponseReceieved: (data, responseCode) => {
                if (!data || responseCode !== 200) {
                    setError("Couldn't load brochures");
                    return;
                }
                const matches = data.filter((row) => (String(row.course_name || "General").trim() || "General") === course);
                setRows(matches);
                if (matches.length) {
                    setFolder({
                        name: course,
                        description: matches.find((row) => row.course_description)?.course_description || "",
                        image: matches.find((row) => row.course_image)?.course_image || "",
                    });
                }
            },
        });
    }, [course, requestAPI]);

    useEffect(() => {
        loadRows();
    }, [loadRows]);

    return (
        <div className="flex flex-column h-full">
            <PageTitle title={course} />
            <TabHeader
                className="px-3 pt-3"
                title={course}
                highlights={["Each file is one brochure card inside this course folder"]}
                actionItems={[
                    <Button icon="pi pi-pencil" severity="secondary" onClick={() => setFolderDialog(true)} />,
                    <Button icon="pi pi-plus" severity="warning" onClick={() => setDialog({ visible: true, record: { course_name: folder.name, course_description: folder.description, course_image: folder.image, file_type: "PDF", is_published: true } })} />,
                ]}
            />
            <Divider />
            {loading ? <Loading /> : error ? <NoContent error={error} /> : (
                <div className="flex-1 overflow-y-scroll flex flex-column gap-2 p-2">
                    {rows?.length ? rows.map((row) => (
                        <Row key={row.id} row={row} setRows={setRows} requestAPI={requestAPI} showToast={showToast} openEdit={(record) => setDialog({ visible: true, record })} />
                    )) : <NoContent />}
                </div>
            )}
            {dialog.visible ? <Editor dialog={dialog} folder={folder} setDialog={setDialog} setRows={setRows} requestAPI={requestAPI} showToast={showToast} /> : null}
            {folderDialog ? <FolderEditor folder={folder} rows={rows} setFolder={setFolder} setRows={setRows} setFolderDialog={setFolderDialog} requestAPI={requestAPI} showToast={showToast} navigate={navigate} /> : null}
        </div>
    );
}

function Row({ row, setRows, requestAPI, showToast, openEdit }) {
    const [deleting, setDeleting] = useState();
    const remove = () => {
        requestAPI({
            requestPath: `website/brochures/${row.id}`,
            requestMethod: "DELETE",
            setLoading: setDeleting,
            parseResponseBody: false,
            onResponseReceieved: (_, responseCode) => {
                if (responseCode === 204) {
                    showToast({ severity: "success", summary: "Deleted", detail: "Brochure removed", life: 1000 });
                    setRows((prev) => prev?.filter((item) => item.id !== row.id));
                } else showToast({ severity: "error", summary: "Failed", detail: "Failed To Delete !", life: 2000 });
            },
        });
    };
    const active = row.is_published !== false && row.is_published !== 0;
    return (
        <div className={`flex gap-3 align-items-center border-round py-2 px-3 text-white ${active ? "bg-green-500" : "bg-red-500"}`}>
            <div className="flex flex-column flex-1 overflow-hidden">
                <span className={`${TEXT_NORMAL} font-semibold`}>{row.title || "Untitled"}</span>
                {row.file_type ? <span className={TEXT_SMALL}>{row.file_type}</span> : null}
            </div>
            <i className="pi pi-pencil cursor-pointer" onClick={() => openEdit(row)} />
            <ProgressiveControl loading={deleting} control={<ConfirmationWrapper action={remove}><i className="pi pi-trash cursor-pointer" /></ConfirmationWrapper>} />
        </div>
    );
}

function Editor({ dialog, folder, setDialog, setRows, requestAPI, showToast }) {
    const [record, setRecord] = useState({ file_type: "PDF", is_published: true, course_name: folder.name, course_description: folder.description, course_image: folder.image, ...(dialog.record || {}) });
    const [saving, setSaving] = useState();
    const isNew = !record.id;
    const kinds = FILE_KINDS.some((item) => item.value === record.file_type) ? FILE_KINDS : [{ label: record.file_type, value: record.file_type }, ...FILE_KINDS];
    const save = () => {
        if (!String(record.title || "").trim()) {
            showToast({ severity: "error", summary: "Name required", detail: "Enter the brochure title", life: 2000 });
            return;
        }
        if (!String(record.file_url || "").trim()) {
            showToast({ severity: "error", summary: "File required", detail: "Upload the brochure file", life: 2000 });
            return;
        }
        const pageCount = String(record.page_count ?? "").trim();
        requestAPI({
            requestPath: "website/brochures",
            requestMethod: isNew ? "POST" : "PATCH",
            requestPostBody: {
                ...record,
                course_name: folder.name,
                course_description: folder.description,
                course_image: folder.image,
                file_type: record.file_type || "PDF",
                page_count: pageCount === "" ? null : pageCount,
            },
            setLoading: setSaving,
            onRequestFailure: () => showToast({ severity: "error", summary: "Failed", detail: "Failed To Save !", life: 2000 }),
            onResponseReceieved: (saved, responseCode) => {
                if (saved && (responseCode === 200 || responseCode === 201)) {
                    setRows((prev) => (isNew ? [...(prev || []), saved] : prev.map((item) => (item.id === saved.id ? saved : item))));
                    showToast({ severity: "success", summary: "Saved", detail: "Brochure saved", life: 1000 });
                    setDialog({ visible: false });
                } else showToast({ severity: "error", summary: "Failed", detail: saved?.error || "Failed To Save !", life: 2000 });
            },
        });
    };
    return (
        <Dialog header={isNew ? "Add a brochure" : "Edit brochure"} visible className="w-11" onHide={() => setDialog({ visible: false })}>
            <div className="flex flex-column">
                <div>
                    <FloatLabel className="mt-5">
                        <InputText id="title" className="w-full" value={record?.title ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, title: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                        <label htmlFor="title">Brochure title</label>
                    </FloatLabel>
                    <FieldNote text="The heading on the brochure card and on the viewer page." />
                </div>
                <div>
                    <FloatLabel className="mt-5">
                        <InputTextarea id="description" className="w-full" rows={3} autoResize value={record?.description || ""} onChange={(event) => setRecord((prev) => ({ ...prev, description: event.target.value }))} disabled={saving} />
                        <label htmlFor="description">Short description</label>
                    </FloatLabel>
                    <FieldNote text="Shown under the title on the card." />
                </div>
                <WebsiteMediaInput label="Brochure file" type="document" field="file_url" hint="PDF, Word, PowerPoint, Excel, image, or ZIP. This is the file opened by View Brochure and Download." record={record} setRecord={setRecord} disabled={saving} />
                <div>
                    <FloatLabel className="mt-5">
                        <Dropdown inputId="file_type" className="w-full" options={kinds} optionLabel="label" optionValue="value" value={record?.file_type || "PDF"} onChange={(event) => setRecord((prev) => ({ ...prev, file_type: event.value }))} disabled={saving} />
                        <label htmlFor="file_type">File kind</label>
                    </FloatLabel>
                    <FieldNote text="The small label on the cover, such as PDF or Word." />
                </div>
                <WebsiteMediaInput label="Cover image" field="cover_image" hint="Required size: 1280 ? 800 px (16:10). A larger photo is scaled to fit this brochure card. The whole photo stays visible." record={record} setRecord={setRecord} disabled={saving} />
                <div>
                    <FloatLabel className="mt-5">
                        <InputText id="cover_alt" className="w-full" value={record?.cover_alt ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, cover_alt: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                        <label htmlFor="cover_alt">Cover description</label>
                    </FloatLabel>
                    <FieldNote text="Read by screen readers. Describe the cover." />
                </div>
                <div>
                    <FloatLabel className="mt-5">
                        <InputText id="page_count" className="w-full" value={record?.page_count ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, page_count: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                        <label htmlFor="page_count">Number of pages</label>
                    </FloatLabel>
                    <FieldNote text="Optional. Example: 12. Shown as 12 Pages." />
                </div>
                <div>
                    <FloatLabel className="mt-5">
                        <InputText id="file_size" className="w-full" value={record?.file_size ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, file_size: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                        <label htmlFor="file_size">File size</label>
                    </FloatLabel>
                    <FieldNote text="Optional. Example: 2.4 MB." />
                </div>
                <div>
                    <FloatLabel className="mt-5">
                        <InputText id="year_label" className="w-full" value={record?.year_label ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, year_label: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                        <label htmlFor="year_label">Year</label>
                    </FloatLabel>
                    <FieldNote text="Optional badge. Example: 2025-26." />
                </div>
                <div>
                    <FloatLabel className="mt-5">
                        <InputText id="language" className="w-full" value={record?.language ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, language: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                        <label htmlFor="language">Language</label>
                    </FloatLabel>
                    <FieldNote text="Optional badge. Example: English." />
                </div>
                <div>
                    <FloatLabel className="mt-5">
                        <InputText id="badge" className="w-full" value={record?.badge ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, badge: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                        <label htmlFor="badge">Highlight label</label>
                    </FloatLabel>
                    <FieldNote text="Optional orange label. Example: New." />
                </div>
                <div className="flex align-items-center gap-2 mt-4">
                    <Checkbox inputId="is_published" checked={record?.is_published !== false && record?.is_published !== 0} onChange={(event) => setRecord((prev) => ({ ...prev, is_published: event.checked }))} disabled={saving} />
                    <label htmlFor="is_published" className={TEXT_SMALL}>Show this brochure on the website</label>
                </div>
                <Button className="mt-5 align-self-start" label="Save" icon="pi pi-check" loading={saving} onClick={save} />
            </div>
        </Dialog>
    );
}

function FolderEditor({ folder, rows, setFolder, setRows, setFolderDialog, requestAPI, showToast, navigate }) {
    const [record, setRecord] = useState(folder);
    const [saving, setSaving] = useState();
    const save = () => {
        const name = String(record.name || "").trim();
        if (!name) {
            showToast({ severity: "error", summary: "Name required", detail: "Enter the course folder name", life: 2000 });
            return;
        }
        if (!rows?.length) {
            setFolder({ name, description: record.description || "", image: record.image || "" });
            setFolderDialog(false);
            navigate(`/manage-website/brochures/folders/${encodeURIComponent(name)}`, { replace: true, state: { course_description: record.description, course_image: record.image } });
            return;
        }
        setSaving(true);
        let pending = rows.length;
        let failed = false;
        rows.forEach((row) => {
            requestAPI({
                requestPath: "website/brochures",
                requestMethod: "PATCH",
                requestPostBody: { ...row, course_name: name, course_description: record.description || "", course_image: record.image || "" },
                onRequestFailure: () => {
                    failed = true;
                    pending -= 1;
                    if (pending === 0) setSaving(false);
                },
                onResponseReceieved: (saved, responseCode) => {
                    if (saved && responseCode === 200) setRows((prev) => prev.map((item) => (item.id === saved.id ? saved : item)));
                    else failed = true;
                    pending -= 1;
                    if (pending === 0) {
                        setSaving(false);
                        if (failed) showToast({ severity: "error", summary: "Failed", detail: "Failed To Save !", life: 2000 });
                        else {
                            showToast({ severity: "success", summary: "Saved", detail: "Course folder saved", life: 1000 });
                            setFolderDialog(false);
                            navigate(`/manage-website/brochures/folders/${encodeURIComponent(name)}`, { replace: true });
                        }
                    }
                },
            });
        });
    };
    return (
        <Dialog header="Edit course folder" visible className="w-11" onHide={() => setFolderDialog(false)}>
            <div className="flex flex-column">
                <div>
                    <FloatLabel className="mt-5">
                        <InputText id="folder_name" className="w-full" value={record?.name ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, name: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                        <label htmlFor="folder_name">Course folder name</label>
                    </FloatLabel>
                    <FieldNote text="Shown on the e-Brochures page and as the heading inside this folder." />
                </div>
                <div>
                    <FloatLabel className="mt-5">
                        <InputTextarea id="folder_description" className="w-full" rows={3} autoResize value={record?.description || ""} onChange={(event) => setRecord((prev) => ({ ...prev, description: event.target.value }))} disabled={saving} />
                        <label htmlFor="folder_description">Line under the folder name</label>
                    </FloatLabel>
                </div>
                <WebsiteMediaInput label="Folder picture" field="image" hint="Required size: 256 ? 256 px (square). A larger picture is scaled to fit this small square. The whole picture stays visible." record={record} setRecord={setRecord} disabled={saving} />
                <Button className="mt-5 align-self-start" label="Save" icon="pi pi-check" loading={saving} onClick={save} />
            </div>
        </Dialog>
    );
}
